import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core';

import { PatternlibService } from '../../global/runtime';
import { ClassNames, LhClose, LhOpen } from '../../utils/interfaces';
import * as LhMath from '../../utils/math';
import { returnScreenSize, ScreenSize } from '../../utils/screen';

export type Position = 'top' | 'bottom' | 'right' | 'left' | 'auto';
export type ResolvedPosition = Exclude<Position, 'auto'>;

@Component({
  tag: 'patternlib-overlay-container',
  styleUrl: 'patternlib-overlay-container.scss',
  shadow: true,
})
export class PatternlibOverlayContainer {
  /**
   * If target is 'element', the flyout is positioned around the 'element' slot.
   * If target is 'center', the flyout is positioned at the center of the window.
   */
  @Prop() target: 'element' | 'center' = 'center';

  /**
   * If an arrow is shown between element and flyout.
   * Only works if target is 'element'.
   */
  @Prop() showArrow = false;

  /**
   * Set the variant.
   */
  @Prop() variant: 'dark' | 'light' = 'light';

  /**
   * Fit content width. Sets the width of the content. If 'element' is set, the width of the slot='element' is used.
   * This logic is overridden if contentWidth is set.
   */
  @Prop() fit: 'auto' | 'element' = 'auto';

  /**
   * Show flyout property. Changes are watched.
   */
  @Prop() show: boolean | string = false;

  /**
   * Close flyout if a window scroll event was received. Only works with `target='element'`.
   */
  @Prop() closeOnScroll = true;

  /**
   * Disables the button and click functionality.
   */
  @Prop() disabled = false;

  /**
   * If true, the `overlay-container` does not attach any click event handlers to open the flyout on `element` click.
   * This allows the implementation side to handle any open or close logic themselves.
   */
  @Prop() disableClickHandlers = false;

  /**
   * Host width.
   */
  @Prop() width = 'auto';

  /**
   * Content width basing on external retrieved width, e.g. a parent object
   */
  @Prop() contentWidth?: number;

  /**
   * Set the position of the flyout.
   */
  @Prop() position: Position | [mobile: Position, tablet: Position, desktop: Position] = 'auto';

  /**
   * Fix the alignment of the flyout.
   */
  @Prop() alignment?: 'right' | 'center' | 'left' | 'auto' = 'auto';

  /**
   * Additional offset added to the y axis if the flyout position is top.
   */
  @Prop() offsetTopY = 0;

  /**
   * Additional offset added to the y axis if the flyout position is bottom.
   */
  @Prop() offsetBottomY = 0;

  /**
   * Additional offset added to the X axis if the flyout position is right.
   */
  @Prop() offsetLeftX = 0;

  /**
   * Additional offset added to the X axis if the flyout position is left.
   */
  @Prop() offsetRightX = 0;

  /**
   * Wether the component is registered at the FlyoutGroups handler or not.
   */
  @Prop() useFlyoutGroups = false;

  /**
   * Flyout group name. Only works if useFlyoutGroups is true.
   */
  @Prop() groupName?: string;

  /**
   * Only updates to the styles triggers a re-rendering.
   */
  @State() contentStyles = this.getDefaultStyles();

  /**
   * Host element ref.
   */
  @Element() hostElement: HTMLElement;

  /**
   * Triggers when the flyout is opened.
   * Set bubbles to false to prevent other components to receive that lhOpen-Event (unwanted side-effects).
   */
  @Event({ eventName: 'lhOpen', bubbles: false }) open: EventEmitter<LhOpen>;

  /**
   * Triggers when the flyout is closed.
   */
  @Event({ eventName: 'lhClose' }) close: EventEmitter<LhClose>;

  /** Size/offset of the arrow between container and element */
  private arrowOffset = 15;

  private flyoutPosition: ResolvedPosition = 'bottom';
  private flyoutAlignment: 'right' | 'center' | 'left' = 'center';

  private elementTargetRef: HTMLDivElement | null = null;
  private contentElementRef: HTMLDivElement | null = null;

  private showFlyout = false;
  private isOpening = false;
  private initialConnect = false;

  connectedCallback() {
    if (this.useFlyoutGroups && this.initialConnect) {
      PatternlibService.registerFlyout(this, this.groupName);
    }
    this.initialConnect = true;
  }

  componentWillLoad() {
    this.showFlyout = this.show === 'true' || this.show === true;

    if (this.showFlyout) {
      setTimeout(() => this.calculateContentStyles(), 0); // Next tick, to get correct references and values.
    } else {
      this.calculateContentStyles();
    }
  }

  componentDidLoad() {
    window.addEventListener(
      'scroll',
      () => {
        if (this.showFlyout && this.target === 'element') {
          if (this.closeOnScroll) {
            this.internalCloseSync();
          } else {
            this.calculateContentStyles();
          }
        }
      },
      true, // capture
    );

    window.addEventListener('resize', () => {
      if (this.showFlyout) {
        this.calculateContentStyles();
      }
    });

    // Close on outside click. Also only if the flyout was not opened in the same lifecycle.
    window.addEventListener(
      'click',
      (e: Event) => {
        if (!this.disableClickHandlers && this.showFlyout && !this.isOpening) {
          const target = e.target as HTMLElement;
          if (!this.hostElement.contains(target)) {
            this.internalCloseSync();
          }
        }
      },
      false,
    );
    if (this.useFlyoutGroups) {
      PatternlibService.registerFlyout(this, this.groupName);
    }
  }

  componentDidRender() {
    this.isOpening = false;
  }

  disconnectedCallback() {
    if (this.useFlyoutGroups) {
      PatternlibService.unregisterFlyout(this, this.groupName);
    }
  }

  @Watch('show')
  async showWatcher(newValue: boolean | string, _oldValue: boolean | string): Promise<void> {
    if ((_oldValue === false || _oldValue === 'false') && (newValue === true || newValue === 'true')) {
      await this.openFlyout();
    }

    if ((_oldValue === true || _oldValue === 'true') && (newValue === false || newValue === 'false')) {
      await this.closeFlyout();
    }
  }

  /**
   * Programmatically open the flyout.
   */
  @Method()
  async openFlyout(): Promise<void> {
    this.internalOpenSync(false);
  }

  /**
   * Programmatically close the flyout.
   */
  @Method()
  async closeFlyout(): Promise<void> {
    this.internalCloseSync();
  }

  /**
   * Programmatically trigger a recalculation of the flyout position and alignment.
   */
  @Method()
  async updateFlyout(): Promise<void> {
    this.calculateContentStyles();
  }

  /**
   * Programmatically cloes all other flyout menus in registered group.
   */

  @Method()
  async closeFlyoutmenu(): Promise<void> {
    this.internalCloseSync();
  }

  private internalOpenSync = (toggle: boolean): void => {
    if (!this.disabled) {
      if (toggle) {
        this.showFlyout = !this.showFlyout;
      } else {
        this.showFlyout = true;
      }

      this.isOpening = this.showFlyout;
      this.calculateContentStyles();

      if (this.showFlyout === true) {
        this.open.emit();
      } else {
        this.close.emit();
      }
      if (this.useFlyoutGroups && this.showFlyout) {
        PatternlibService.closeAllOtherFlyouts(this, this.groupName);
      }
    }
  };

  private internalCloseSync = (): void => {
    this.showFlyout = false;
    this.calculateContentStyles();

    this.close.emit();
  };

  private onTargetClick = e => {
    if (!this.disableClickHandlers) {
      e.stopPropagation();
      this.internalOpenSync(true);
    }
  };

  /** Prevent space triggering onclick */
  private handleKeyUp = e => {
    if (e.code == 'Space') {
      e.preventDefault();
    }
  };

  private onBackdropClick = e => {
    if (!this.disableClickHandlers) {
      e.stopPropagation();
      this.internalCloseSync();
    }
  };

  private calculateContentStyles(): void {
    let styles: { [key: string]: string } = {
      opacity: '0.0',
      visibility: 'hidden',
      position: 'absolute',
      left: '-99999px',
      top: '-99999px',
    };

    if (this.contentElementRef != null) {
      this.calculatePositionAndAlignment();

      switch (this.target) {
        case 'element':
          styles = this.getElementTargetContentStyles();
          break;

        case 'center':
          styles = this.getPositionalTargetContentStyles();
      }
    }

    this.contentStyles = styles;
  }

  /**
   * Calculates the styles if target is positional (like 'center').
   */
  private getPositionalTargetContentStyles(): { [key: string]: string } {
    let style = this.getDefaultStyles();
    const contentRect = this.contentElementRef.getBoundingClientRect();

    if (this.target === 'center') {
      style = {
        ...style,
        left: `${(window.innerWidth - contentRect.width) / 2}px`,
        top: `${(window.innerHeight - contentRect.height) / 2}px`,
      };
    }

    return style;
  }

  private getDefaultStyles(): { [key: string]: string } {
    return {
      visibility: this.showFlyout ? 'visible' : 'hidden',
      position: this.showFlyout ? 'fixed' : 'absolute',
      opacity: this.showFlyout ? '1.0' : '0.0',
      left: '-9999px',
      top: '-99999px',
    };
  }

  /**
   * Calculates the styles if target is 'element'.
   */
  private getElementTargetContentStyles() {
    let styles = this.getDefaultStyles();

    if (this.elementTargetRef != null && this.showFlyout === true) {
      const targetRect = this.elementTargetRef.getBoundingClientRect();
      const contentRect = this.contentElementRef.getBoundingClientRect();

      if (this.fit === 'element') {
        contentRect.width = targetRect.width;
      }

      const availableWidth = window.innerWidth;

      let flyoutContentWidth = this.contentWidth != null ? this.contentWidth : contentRect.width;
      flyoutContentWidth = Math.min(flyoutContentWidth, availableWidth);

      let top: number | 'auto' = targetRect.top;
      let bottom: number | 'auto' = targetRect.bottom;
      let left = targetRect.left;

      if (this.flyoutPosition === 'bottom') {
        bottom = 'auto';
        top = top + targetRect.height + (this.showArrow ? this.arrowOffset : 0) + this.offsetBottomY;
      } else if (this.flyoutPosition === 'right') {
        left = left + targetRect.width + (this.showArrow ? this.arrowOffset : 0) + this.offsetLeftX;
        bottom = 'auto';
      } else if (this.flyoutPosition === 'left') {
        left = left - flyoutContentWidth - (this.showArrow ? this.arrowOffset : 0) - this.offsetRightX;
        bottom = 'auto';
      } else {
        top = 'auto';
        bottom = window.innerHeight - targetRect.top + (this.showArrow ? this.arrowOffset : 0) - this.offsetTopY;
      }

      // align them horizontal only when position is not right or left beneath the content (it doesn't make sense in
      // those cases to align them horizontal and may lead to overlapping the origin element).
      if (this.flyoutPosition !== 'right' && this.flyoutPosition !== 'left') {
        // adjust alignment offsets.
        if (this.flyoutAlignment === 'center') {
          const center = targetRect.left + targetRect.width / 2;
          left = center - flyoutContentWidth / 2;
        } else if (this.flyoutAlignment === 'right') {
          const right = targetRect.left + targetRect.width;
          left = right - flyoutContentWidth;
        }
      }

      // Cleanup position and content width based on if we are too close to screen borders.
      if (flyoutContentWidth >= availableWidth - 5) {
        left = 5;
        flyoutContentWidth = availableWidth - 5;
      } else if (left + flyoutContentWidth >= availableWidth - 5) {
        left = availableWidth - flyoutContentWidth - 22.5;
      }

      // Constrain within a 5px padding to the left and right.
      left = Math.max(left, 5);
      left = Math.min(left, availableWidth - flyoutContentWidth - 10);

      // Offset in px from the left of the flyout content to the target center.
      this.hostElement.style.setProperty('--arrow-from-left', `${targetRect.left - left + targetRect.width / 2 - 12}px`);
      this.hostElement.style.setProperty('--arrow-offset', `${targetRect.width / 2}px`);

      styles = {
        ...styles,
        position: 'fixed',
        top: top === 'auto' ? top : `${top}px`,
        bottom: bottom === 'auto' ? bottom : `${bottom}px`,
        left: `${left}px`,
        width: `${flyoutContentWidth}px`,
      };
    }

    return styles;
  }

  /**
   * Calculates the Flyout position (vertical) and alignment (horizontal).
   */
  private calculatePositionAndAlignment(): void {
    const viewportCenter = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    const rect = this.elementTargetRef.getBoundingClientRect();
    const targetCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    const angle = LhMath.angleBetweenVec2(viewportCenter, targetCenter);
    const screenSize = returnScreenSize();

    const breakpointPosition = this.getFinalPosition(screenSize);
    this.flyoutPosition = this.resolvePosition(breakpointPosition, angle);

    this.flyoutAlignment = this.alignment == null || this.alignment == 'auto' ? LhMath.getAlignmentPosition(angle) : this.alignment;

    // Adjust alignment based on position to the left and right borders.
    // TODO: Would be better to get the size of the content slot so "slotWidth / 2" would be the correct value here.
    if (targetCenter.x < 100) {
      this.flyoutAlignment = 'left';
    } else if (window.innerWidth - targetCenter.x < 100) {
      this.flyoutAlignment = 'right';
    }
  }

  /**
   * Extracts the position, even if position is a breakpoint specific array.
   */
  private getFinalPosition(screenSize: ScreenSize): Position {
    if (Array.isArray(this.position)) {
      switch (screenSize) {
        case 'isMobile':
          return this.position[0];
        case 'isTablet':
          return this.position[1];
        case 'isDesktop':
          return this.position[2];
      }
    }

    return this.position;
  }

  private resolvePosition(pos: Position, angle: number): ResolvedPosition {
    if (pos == 'auto') {
      return angle >= 0 ? 'top' : 'bottom';
    }

    return pos;
  }

  render() {
    const contentClasses: ClassNames = {
      'content': true,
      'arrow': this.showArrow && this.target === 'element' && this.showFlyout,
      'z-10': true,
      [this.variant]: true,
    };

    if (this.target === 'element') {
      contentClasses[this.flyoutPosition] = true;
    } else {
      contentClasses['window'] = true;
    }

    return (
      <Host style={{ width: this.width }}>
        <div class="container">
          <div onKeyUp={this.handleKeyUp} onClick={this.onTargetClick} ref={el => (this.elementTargetRef = el as HTMLDivElement)} class="flex">
            <slot name="element"></slot>
          </div>

          <div
            class="backdrop"
            onClick={this.onBackdropClick}
            style={{ display: this.target != 'element' && this.showFlyout ? 'block' : 'none', position: this.showFlyout ? 'fixed' : 'absolute' }}
          ></div>
          <div class={contentClasses} style={this.contentStyles} ref={el => (this.contentElementRef = el as HTMLDivElement)}>
            <slot name="content"></slot>
          </div>
        </div>
      </Host>
    );
  }
}
